import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, Repeat, Send, CheckCircle, Eye, Bell, Users, Layers, Workflow } from 'lucide-react';
import { Badge } from '../components/ui/badge';

import CyclePatternsManager from '../components/CyclePatternsManager';
import EmployeeScheduleCalendar from '../components/EmployeeScheduleCalendar';
import ScheduleOverrideRequest from '../components/ScheduleOverrideRequest';
import ScheduleOverrideApproval from '../components/ScheduleOverrideApproval';
import AttendanceViewWithColors from '../components/AttendanceViewWithColors';
import PlanningNotifications from '../components/PlanningNotifications';
import ScheduleAssignmentManager from '../components/ScheduleAssignmentManager';
import ScheduleManager from '../components/ScheduleManager';
import PlanningNavigationCard from '../components/planning/PlanningNavigationCard';
import PlanningInfoFooter from '../components/planning/PlanningInfoFooter';

const sectionColors = {
  Configuration: {
    bg: 'bg-primary/10', border: 'border-primary/40', borderLeft: 'border-l-primary', text: 'text-primary',
    buttonActive: 'bg-primary text-white border-primary', buttonHover: 'hover:bg-primary/10 hover:border-primary/40 hover:text-primary',
    badgeBg: 'bg-primary/20', badgeText: 'text-primary',
  },
  Assignment: {
    bg: 'bg-muted', border: 'border-border', borderLeft: 'border-l-muted-foreground', text: 'text-muted-foreground',
    buttonActive: 'bg-secondary text-secondary-foreground border-border', buttonHover: 'hover:bg-accent hover:border-border hover:text-foreground',
    badgeBg: 'bg-muted', badgeText: 'text-muted-foreground',
  },
  Visualization: {
    bg: 'bg-green-950/20', border: 'border-green-900/40', borderLeft: 'border-l-green-500', text: 'text-green-400',
    buttonActive: 'bg-green-600 text-white border-green-600', buttonHover: 'hover:bg-green-950/20 hover:border-green-900/40 hover:text-green-400',
    badgeBg: 'bg-green-950/30', badgeText: 'text-green-400',
  },
  Requests: {
    bg: 'bg-orange-950/20', border: 'border-orange-900/40', borderLeft: 'border-l-orange-500', text: 'text-orange-400',
    buttonActive: 'bg-orange-600 text-white border-orange-600', buttonHover: 'hover:bg-orange-950/20 hover:border-orange-900/40 hover:text-orange-400',
    badgeBg: 'bg-orange-950/30', badgeText: 'text-orange-400',
  },
  Approval: {
    bg: 'bg-purple-950/20', border: 'border-purple-900/40', borderLeft: 'border-l-purple-500', text: 'text-purple-400',
    buttonActive: 'bg-purple-600 text-white border-purple-600', buttonHover: 'hover:bg-purple-950/20 hover:border-purple-900/40 hover:text-purple-400',
    badgeBg: 'bg-purple-950/30', badgeText: 'text-purple-400',
  },
};

const Planning = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'calendar';
  const setActiveTab = (tab) => setSearchParams({ tab }, { replace: true });

  const tabs = [
    { id: 'schedules',     label: t('planning.tabs.schedules'),     icon: Layers,       description: t('planning.tabs.schedulesDesc'),     section: 'Configuration', step: 1 },
    { id: 'cycles',        label: t('planning.tabs.cycles'),        icon: Repeat,       description: t('planning.tabs.cyclesDesc'),        section: 'Configuration', step: 1 },
    { id: 'assignments',   label: t('planning.tabs.assignments'),   icon: Users,        description: t('planning.tabs.assignmentsDesc'),   section: 'Assignment',    step: 2 },
    { id: 'calendar',      label: t('planning.tabs.calendar'),      icon: Calendar,     description: t('planning.tabs.calendarDesc'),      section: 'Visualization', step: 3 },
    { id: 'attendance',    label: t('planning.tabs.attendance'),    icon: Eye,          description: t('planning.tabs.attendanceDesc'),    section: 'Visualization', step: 3 },
    { id: 'requests',      label: t('planning.tabs.requests'),      icon: Send,         description: t('planning.tabs.requestsDesc'),      section: 'Requests',      step: 4 },
    { id: 'approvals',     label: t('planning.tabs.approvals'),     icon: CheckCircle,  description: t('planning.tabs.approvalsDesc'),     section: 'Approval',      step: 5, badge: t('planning.tabs.managerBadge') },
    { id: 'notifications', label: t('planning.tabs.notifications'), icon: Bell,         description: t('planning.tabs.notificationsDesc'), section: 'Approval',      step: 5 },
  ];

  const sections = [
    { id: 'Configuration', name: t('planning.sections.configuration'), step: 1, tabs: tabs.filter((t) => t.section === 'Configuration') },
    { id: 'Assignment',    name: t('planning.sections.assignment'),    step: 2, tabs: tabs.filter((t) => t.section === 'Assignment') },
    { id: 'Visualization', name: t('planning.sections.visualization'), step: 3, tabs: tabs.filter((t) => t.section === 'Visualization') },
    { id: 'Requests',      name: t('planning.sections.requests'),      step: 4, tabs: tabs.filter((t) => t.section === 'Requests') },
    { id: 'Approval',      name: t('planning.sections.approval'),      step: 5, tabs: tabs.filter((t) => t.section === 'Approval') },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'calendar':      return <EmployeeScheduleCalendar />;
      case 'cycles':        return <CyclePatternsManager />;
      case 'schedules':     return <ScheduleManager />;
      case 'assignments':   return <ScheduleAssignmentManager />;
      case 'requests':      return <ScheduleOverrideRequest />;
      case 'approvals':     return <ScheduleOverrideApproval />;
      case 'attendance':    return <AttendanceViewWithColors />;
      case 'notifications': return <PlanningNotifications />;
      default:              return <EmployeeScheduleCalendar />;
    }
  };

  const currentTab = tabs.find((tb) => tb.id === activeTab);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold leading-7 text-foreground sm:text-3xl">{t('planning.title')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('planning.subtitle')}</p>
        </div>
        <Badge variant="outline" className="flex items-center gap-1.5 px-3 py-1.5">
          <Workflow className="h-3.5 w-3.5" />
          <span className="font-medium">{t('planning.badge')}</span>
        </Badge>
      </div>

      <PlanningNavigationCard
        sections={sections}
        sectionColors={sectionColors}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        currentTab={currentTab}
        renderTabContent={renderTabContent}
      />

      <PlanningInfoFooter />
    </div>
  );
};

export default Planning;
